import useCustomerInfo from "@repo/shopify/hooks/useCustomerInfo";
import useCustomerInfoCaa from "@repo/shopify/hooks/useCustomerInfoCaa";
import type { ICaaCustomerAddress } from "@repo/shopify/queries/customer-account-gql";
import useCustomerQueryCaa from "@repo/shopify/queries/useCustomerQueryCaa";
import { extractId } from "@repo/shopify/utils/common";
import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { URLS } from "@const/urls";

import useCustomerQuery from "@hooks/useCustomerQuery";

import apolloCaaClient from "@lib/apolloCaa";

import { useCustomerStore } from "@store/customer";

export default function useAddresses(isJp: boolean) {
  const navigate = useNavigate();

  const { customer, token, caaCustomer, updateCaa } = useCustomerStore();

  const { fetchCustomer } = useCustomerQuery();
  const { fetchCaaCustomer } = useCustomerQueryCaa({ client: apolloCaaClient });

  const { isPartnership, addresses: classicAddresses } = useCustomerInfo(customer, "thisisneverthat");
  const { addresses: caaAddresses } = useCustomerInfoCaa(caaCustomer ?? { id: "", tags: [] }, "thisisneverthat");

  const [showForm, setShowForm] = useState(false);
  const [editableAddress, setEditableAddress] = useState<MailingAddress | ICaaCustomerAddress | undefined>();

  useEffect(() => {
    if (isJp) {
      if (!caaCustomer) navigate(URLS.HOME);
      return;
    }

    if (!customer || isPartnership) navigate(URLS.HOME);
  }, [isJp, customer, isPartnership, caaCustomer, navigate]);

  const onCompletedMutation = async () => {
    if (isJp) {
      const { data } = await fetchCaaCustomer();
      if (data?.customer) updateCaa(data.customer);
      return;
    }

    await fetchCustomer({ variables: { customerAccessToken: token.accessToken } });
  };

  const openAddressForm = (address: MailingAddress | ICaaCustomerAddress | undefined) => {
    setShowForm(true);
    setEditableAddress(address);
  };

  const defaultAddress = isJp ? caaCustomer?.defaultAddress : customer.defaultAddress;

  const otherAddresses = (isJp ? caaAddresses : classicAddresses).filter(
    (node) => extractId(node.id) !== extractId(String(defaultAddress?.id)),
  );

  return {
    showForm,
    setShowForm,
    editableAddress,
    setEditableAddress,
    onCompletedMutation,
    openAddressForm,
    defaultAddress,
    otherAddresses,
  };
}
